import type { ScanInboxResult } from '@auto-oms/shared/types/batch';
import { formatDateTime } from '../../lib/format';

interface Props {
  open: boolean;
  result: ScanInboxResult | null;
  onClose: () => void;
}

export function ScanResultDialog({ open, result, onClose }: Props) {
  if (!open || !result) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-ink-950/40 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sr-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl bg-paper shadow-modal sm:max-h-[80vh] sm:rounded-2xl">
        {/* 헤더 */}
        <div className="flex shrink-0 flex-col gap-3 border-b border-ink-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 sm:py-5">
          <div className="flex flex-wrap items-center gap-2">
            <p className="label-overline">Step 3 · 송장</p>
            <h2 id="sr-title" className="text-[18px] font-medium tracking-[-0.01em] text-ink-950 sm:text-[20px]">
              스캔 결과
            </h2>
            {result.dryRun && (
              <span className="badge bg-warn-soft text-warn-ink">테스트 모드</span>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
            <p className="text-[12px] text-ink-500 sm:text-[13px]">
              메시지 <span className="tabular text-ink-800">{result.scanned}</span> · 매칭{' '}
              <span className="tabular text-brand-700">{result.matched}</span> · 미매칭{' '}
              <span className="tabular text-ink-700">{result.unmatched}</span>
              {result.failed > 0 && (
                <>
                  {' '}
                  · 실패 <span className="tabular text-danger-ink">{result.failed}</span>
                </>
              )}
            </p>
            <button className="btn-secondary btn-sm shrink-0" onClick={onClose}>
              닫기
            </button>
          </div>
        </div>

        {/* 본문 */}
        <div className="space-y-4 overflow-y-auto bg-canvas px-4 py-4 sm:px-6 sm:py-5">
          {result.messages.length === 0 && (
            <p className="py-10 text-center text-[13px] text-ink-400">
              조건에 맞는 메일이 없습니다.
            </p>
          )}
          {result.messages.map((msg, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-ink-200 bg-paper">
              {/* 메일 헤더 */}
              <div className="border-b border-ink-100 bg-ink-50 px-4 py-2.5">
                <p className="truncate text-[13px] font-medium text-ink-900">
                  {msg.subject ?? '(제목 없음)'}
                </p>
                <p className="mt-0.5 text-[11px] text-ink-500">
                  {msg.from ?? '-'}
                  {msg.receivedAt && (
                    <span className="ml-3">{formatDateTime(msg.receivedAt)}</span>
                  )}
                </p>
              </div>

              {/* 파싱 실패 디버그 */}
              {msg.rows.length === 0 && msg.debugParts && (
                <div className="border-b border-ink-100 px-4 py-3">
                  <p className="mb-2 text-[12px] font-medium text-warn-ink">
                    {msg.xlsxFound === false
                      ? '엑셀 첨부파일을 찾지 못했습니다. 메일에 포함된 파트:'
                      : '엑셀 파일을 다운로드했으나 인식 가능한 행이 없습니다. 헤더명(주문번호/상품주문번호/택배사/운송장번호)을 확인하세요.'}
                  </p>
                  <table className="w-full text-[11px] text-ink-600">
                    <thead className="text-ink-400">
                      <tr>
                        <th className="py-0.5 pr-4 text-left font-medium">MIME 타입</th>
                        <th className="py-0.5 pr-4 text-left font-medium">파일명</th>
                        <th className="py-0.5 pr-4 text-left font-medium">크기</th>
                        <th className="py-0.5 text-left font-medium">attachmentId</th>
                      </tr>
                    </thead>
                    <tbody>
                      {msg.debugParts.map((p, k) => (
                        <tr key={k} className="border-t border-ink-100">
                          <td className="py-0.5 pr-4 font-mono">{p.mimeType}</td>
                          <td className="py-0.5 pr-4">{p.filename || '-'}</td>
                          <td className="py-0.5 pr-4 tabular">
                            {p.size > 0 ? `${p.size}B` : '-'}
                          </td>
                          <td className="py-0.5">{p.hasAttachmentId ? '있음' : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {msg.rows.length === 0 && !msg.debugParts ? (
                <p className="px-4 py-3 text-[13px] text-ink-400">
                  엑셀 첨부파일 없음 또는 파싱 가능한 행 없음
                </p>
              ) : (
                msg.rows.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] table-fixed text-[13px]">
                      <thead className="bg-ink-50 text-[11px] uppercase tracking-[0.04em] text-ink-500">
                        <tr>
                          <th className="w-36 px-3 py-2 text-left font-semibold">주문번호</th>
                          <th className="w-36 px-3 py-2 text-left font-semibold">상품주문번호</th>
                          <th className="w-24 px-3 py-2 text-left font-semibold">택배사</th>
                          <th className="px-3 py-2 text-left font-semibold">송장번호</th>
                          <th className="w-16 px-3 py-2 text-center font-semibold">매칭</th>
                        </tr>
                      </thead>
                      <tbody>
                        {msg.rows.map((row, j) => (
                          <tr
                            key={j}
                            className={[
                              'border-t border-ink-100',
                              row.matched ? 'bg-brand-50/60' : 'bg-paper',
                            ].join(' ')}
                          >
                            <td className="px-3 py-2 font-mono text-[12px] text-ink-800">
                              {row.externalOrderId ?? '-'}
                            </td>
                            <td className="px-3 py-2 font-mono text-[12px] text-ink-500">
                              {row.productOrderId ?? '-'}
                            </td>
                            <td className="px-3 py-2 text-ink-800">{row.carrier}</td>
                            <td className="px-3 py-2 font-mono text-[12px] text-ink-800">
                              {row.trackingNumber}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {row.matched ? (
                                <span className="font-medium text-brand-700">매칭</span>
                              ) : (
                                <span className="text-ink-300">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
